import React from 'react';

const ExpectedDepositForm = ({ 
  depositType, 
  setDepositType, 
  depositDay, 
  setDepositDay,
  depositMonth,
  setDepositMonth,
  depositDate,
  setDepositDate,
  frequency 
}) => {
  const depositTypes = [
    { value: 'First', label: 'First' },
    { value: 'Last', label: 'Last' },
    { value: 'Every', label: 'Every' },
    { value: 'Specific', label: 'Specific' }
  ];

  const weekDays = [
    { value: 'Monday', label: 'Monday' },
    { value: 'Tuesday', label: 'Tuesday' },
    { value: 'Wednesday', label: 'Wednesday' },
    { value: 'Thursday', label: 'Thursday' },
    { value: 'Friday', label: 'Friday' },
    { value: 'Saturday', label: 'Saturday' },
    { value: 'Sunday', label: 'Sunday' }
  ];

  const monthDays = Array.from({ length: 31 }, (_, i) => ({
    value: (i + 1).toString(),
    label: (i + 1).toString()
  }));

  const months = [
    { value: 'January', label: 'January' },
    { value: 'February', label: 'February' },
    { value: 'March', label: 'March' },
    { value: 'April', label: 'April' },
    { value: 'May', label: 'May' },
    { value: 'June', label: 'June' },
    { value: 'July', label: 'July' },
    { value: 'August', label: 'August' },
    { value: 'September', label: 'September' },
    { value: 'October', label: 'October' },
    { value: 'November', label: 'November' },
    { value: 'December', label: 'December' }
  ];

  const getAvailableDepositTypes = () => {
    if (frequency === 'OneTime' || frequency === 'Daily') {
      return []; 
    }
    return depositTypes;
  };

  const shouldShowDepositType = () => {
    return frequency !== 'OneTime' && frequency !== 'Daily';
  };

  const shouldShowMonth = () => {
    if (frequency === 'Yearly') {
      return true;
    }
    if (frequency === 'Monthly') {
      return true; 
    }
    return false;
  };

  const shouldShowDay = () => {
    if (frequency === 'Weekly') {
      return depositType === 'Every' || depositType === 'Specific';
    }
    if (frequency === 'Yearly') {
      return true; 
    }
    if (frequency === 'Monthly') {
      return depositType === 'Every' || depositType ==='Specific';
    }
    return false;
  };

  const shouldShowDate = () => {
    return frequency === 'OneTime' || frequency === 'Daily';
  };

  const getDayOptions = () => {
    if (frequency === 'Weekly') {
      return weekDays;
    } else if (frequency === 'Monthly' || frequency === 'Yearly') {
      return monthDays;
    }
    return [];
  };

  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getPreviewText = () => {
    if (frequency === 'OneTime') {
      if (depositDate) {
        return ` On ${new Date(depositDate).toLocaleDateString()} (OneTime)`;
      }
      return ' Select the date when this income will be received';
    }

    if (frequency === 'Daily') {
      if (depositDate) {
        return ` Starting from ${new Date(depositDate).toLocaleDateString()} (daily recurring)`;
      }
      return ' Select the starting date for daily deposits';
    }

    if (frequency === 'Weekly') {
      if (!depositType) return '';
      
      if (depositType === 'First') {
        return ` Every Monday (weekly)`;
      } else if (depositType === 'Last') {
        return ` Every Sunday (weekly)`;
      } else if (depositType === 'Every' || depositType === 'Specific') {
        if (!depositDay) return '';
        return ` Every ${depositDay} (weekly)`;
      }
    }

    if (frequency === 'Monthly') {
      if (!depositType || !depositMonth) return '';
      
      if (depositType === 'First') {
        return ` First day of every month (starting from ${depositMonth})`;
      } else if (depositType === 'Last') {
        return ` Last day of every month (starting from ${depositMonth})`;
      } else if (depositType === 'Every' || depositType === 'Specific') {
        if (!depositDay) return '';
        return ` Every ${depositDay} of the month (starting from ${depositMonth})`;
      }
    }

    if (frequency === 'Yearly') {
      if (!depositType || !depositMonth || !depositDay) return '';
      
      if (depositType === 'First') {
        return ` First day of ${depositMonth} every year`;
      } else if (depositType === 'Last') {
        return ` Last day of ${depositMonth} every year`;
      } else if (depositType === 'Every' || depositType === 'Specific') {
        return ` Every ${depositMonth} ${depositDay} (yearly)`;
      }
    }

    return '';
  };
  React.useEffect(() => {
    if (frequency === 'Daily' && !depositDate) {
      setDepositDate(getCurrentDate());
    }
    if (frequency === 'OneTime' && !depositDate) {
      setDepositDate(getCurrentDate());
    }
  }, [frequency, depositDate, setDepositDate]);

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">Expected Deposit Schedule</h4>
        <p className="text-sm text-blue-700">
          Set when this {frequency?.toLowerCase()} income is typically deposited to automatically update your account balance.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {shouldShowDepositType() && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Deposit Type
            </label>
            <select
              value={depositType}
              onChange={(e) => setDepositType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-btn-primary focus:border-transparent"
            >
              <option value="">Select Type</option>
              {getAvailableDepositTypes().map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {shouldShowDate() && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {frequency === 'OneTime' ? 'Deposit Date' : 'Starting Date'}
            </label>
            <input
              type="date"
              value={depositDate || getCurrentDate()}
              onChange={(e) => setDepositDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-btn-primary focus:border-transparent"
            />
          </div>
        )}

        {shouldShowMonth() && (depositType || frequency === 'Yearly') && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {frequency === 'Monthly' ? 'Starting Month' : 'Month'}
            </label>
            <select
              value={depositMonth || ''}
              onChange={(e) => setDepositMonth(e.target.value)}
              className="w-full px-3  py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-btn-primary focus:border-transparent"
            >
              <option value="">Select Month</option>
              {months.map(month => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {shouldShowDay() && (depositType || frequency === 'Yearly') && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Day
            </label>
            <select
              value={depositDay || ''}
              onChange={(e) => setDepositDay(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-btn-primary focus:border-transparent"
            >
              <option value="">Select Day</option>
              {getDayOptions().map(day => (
                <option key={day.value} value={day.value}>
                  {day.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {(depositType || frequency === 'OneTime' || frequency === 'Daily' || frequency === 'Yearly') && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <p className="text-sm text-green-800">
            <span className="font-medium">Schedule Preview:</span>
            {getPreviewText()}
          </p>
        </div>
      )}

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <p className="text-xs text-amber-800">
          <span className="font-medium">Deposit Schedule Rules:</span>
          <br />
          {frequency === 'Daily' && (
            <>• Choose the starting date - deposits will occur daily from this date</>
          )}
          {frequency === 'OneTime' && (
            <>• Choose the exact date when this income will be received</>
          )}
          {frequency === 'Weekly' && (
            <>
              • <strong>First:</strong> Every Monday<br/>
              • <strong>Last:</strong> Every Sunday<br/>
              • <strong>Every/Specific:</strong> Choose specific weekday
            </>
          )}
          {frequency === 'Monthly' && (
            <>
              • <strong>First:</strong> First day of every month from selected starting month<br/>
              • <strong>Last:</strong> Last day of every month from selected starting month<br/>
              • <strong>Every:</strong> Specific day of every month from selected starting month
            </>
          )}
          {frequency === 'Yearly' && (
            <>
              • Choose the specific month and day when yearly deposit occurs<br/>
              • <strong>First:</strong> First day of selected month every year<br/>
              • <strong>Last:</strong> Last day of selected month every year<br/>
              • <strong>Every:</strong> Specific day of selected month every year
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default ExpectedDepositForm;